/**
 * Os filtros da listagem de Itens, lembrados entre visitas.
 *
 * <p>Existe porque o percurso normal é <b>filtrar, abrir uma ficha, voltar</b> — e
 * voltar para a lista inteira desfaz o filtro que a pessoa acabou de montar. Com oito
 * categorias e centenas de fichas, remontar a busca a cada ida e volta é o bastante
 * para ninguém usar o filtro.
 *
 * <p>Fica no {@code localStorage}, como as sanfonas, e pela mesma razão: é preferência
 * de leitura, do navegador. Não é estado de servidor — duas pessoas olhando a mesma
 * lista querem recortes diferentes.
 *
 * <p>É de <b>Itens</b>, e só. A busca das outras coleções continua zerando ao navegar:
 * lá a lista é uma grade que se lê de uma vez, e um filtro lembrado que ninguém pediu
 * viraria "a listagem está faltando fichas".
 */

/** Onde o navegador guarda os filtros da listagem de Itens. */
const CHAVE = 'gg-pilot:itens:filtros';

export interface FiltrosDeItem {
  busca: string;
  /** `null` é todas; `''` é sem raridade. */
  raridade: string | null;
  /** `null` é todas; `0` é sem região. */
  regiao: number | null;
}

export const FILTROS_VAZIOS: FiltrosDeItem = { busca: '', raridade: null, regiao: null };

/**
 * Lê os filtros gravados, com o vazio como resposta a qualquer imprevisto.
 *
 * <p>Confere campo a campo em vez de confiar no JSON: o que está no disco foi escrito
 * por uma versão anterior desta tela, e vai continuar lá depois da próxima. Um
 * {@code raridade} que virou número por engano precisa cair no padrão, e não descer
 * para dentro do filtro — onde produziria uma lista vazia sem nada explicando.
 */
export function leFiltros(): FiltrosDeItem {
  try {
    const cru = window.localStorage.getItem(CHAVE);
    if (!cru) return FILTROS_VAZIOS;
    const salvo = JSON.parse(cru) as Partial<FiltrosDeItem>;
    return {
      busca: typeof salvo.busca === 'string' ? salvo.busca : '',
      raridade: typeof salvo.raridade === 'string' ? salvo.raridade : null,
      regiao: typeof salvo.regiao === 'number' ? salvo.regiao : null,
    };
  } catch {
    // localStorage bloqueado, ou JSON corrompido. A lista abre inteira, que é o
    // padrão — preferência de leitura não vale um erro na tela.
    return FILTROS_VAZIOS;
  }
}

export function gravaFiltros(filtros: FiltrosDeItem): void {
  try {
    window.localStorage.setItem(CHAVE, JSON.stringify(filtros));
  } catch {
    // Sem persistir, os filtros ainda funcionam nesta visita.
  }
}
